import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'form/styles/FormEditView.style';

import InlineDateField from './InlineDateField';

type DateTextItem = { date: string; text: string };

interface DateTextListFieldProps {
  slug: string;
}

export default function DateTextListField({
  slug,
}: DateTextListFieldProps): JSX.Element {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const { t } = useTranslation('pageForm');
  const [inputCount, setInputCount] = useState(1);

  const stored: DateTextItem[] = Array.isArray(values[slug])
    ? (values[slug] as DateTextItem[])
    : [];
  const count = Math.max(inputCount, stored.length, 1);
  const display: DateTextItem[] = [];
  for (let i = 0; i < count; i += 1) {
    display.push(stored[i] || { date: '', text: '' });
  }

  const save = (next: DateTextItem[]) => setFieldValue(slug, next);

  const handleDateChange = (index: number, date: string) => {
    const next = [...display];
    next[index] = { ...next[index], date };
    save(next);
  };

  const handleTextChange = (index: number, text: string) => {
    const next = [...display];
    next[index] = { ...next[index], text };
    save(next);
  };

  const handleRemove = (index: number) => {
    const next = display.filter((_, i) => i !== index);
    save(next);
    setInputCount(Math.max(count - 1, 1));
  };

  return (
    <Styled.ListFieldWrapper>
      {display.map((item, index) => (
        <Styled.ListRow key={index}>
          <Styled.ListRowItem>
            <InlineDateField
              value={item.date}
              onChange={(next) => handleDateChange(index, next)}
              placeholderText={t('edit.datePlaceholder')}
            />
          </Styled.ListRowItem>
          <Styled.ListRowItem>
            <Styled.Input
              type="text"
              value={item.text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleTextChange(index, e.target.value)
              }
              placeholder={t('edit.placeholder')}
            />
          </Styled.ListRowItem>
          {count > 1 && (
            <Buttons.GhostIcon
              $size="tiny"
              type="button"
              aria-label={t('edit.removeItem')}
              onClick={() => handleRemove(index)}
            >
              <General.XClose />
            </Buttons.GhostIcon>
          )}
        </Styled.ListRow>
      ))}
      <Buttons.Ghost
        $size="small"
        type="button"
        onClick={() => setInputCount(count + 1)}
      >
        <General.Plus />
        {t('edit.addItem')}
      </Buttons.Ghost>
    </Styled.ListFieldWrapper>
  );
}
